import { ChangeEvent, memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SortParams,
  SortParamsArg,
  InvestorSpaceModel,
  Option,
  InvestorSpaceFilters,
} from 'models';
import { CSSProperties } from '@material-ui/styles';
import { investorSpaceTableCells } from 'configs';
import _get from 'lodash/get';
import { customFormat } from 'utils';

import { PageTitle } from 'containers';
import {
  MultipleSelect,
  Centered,
  Loader,
  Error,
  PageLoader,
  FiltersBar,
  ButtonShape,
  Download,
  ControlWrapper,
  TablePagination,
} from 'components';
import TableHeader from 'components/table/table-header';
import {
  Table,
  TextField,
  TableRow,
  TableCell,
  TableBody,
  InputProps,
  styled,
} from '@material-ui/core';

import clsx from 'clsx';
import { useStyles } from './style';

const StyledTableCell = styled(TableCell)({
  padding: 3,
  background: '#fff',
  border: 0,
});

const DefaultTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 10px',
  fontSize: 14,
  fontFamily: 'NeoSansStd',
  background: '#fff',
  color: theme.custom.darkBlue,
}));

function isItemInList(list: Option[], item: Option) {
  return list.some((listItem) => listItem.Value === item.Value);
}

interface Props {
  spacesList: InvestorSpaceModel[];
  coopName?: string | null;
  sortParams: SortParams;
  loading: boolean;
  error: boolean;
  refresh: boolean;
  pageNumber: number;
  totalCount: number;
  pageSize: number;
  downloadLink: string;
  filters: InvestorSpaceFilters;
  statusesOptions: Option[];
  typesOptions: Option[];
  handleChangeCurrentPage: (e: ChangeEvent<unknown>, page: number) => void;
  handleChangeSortParams: (params: SortParamsArg) => void;
  handleResetFilters: () => void;
  handleChangeInput: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeMultiSelect: (option: Option, name?: string) => void;
}

const InvestorSpaceGrid: React.FC<Props> = ({
  spacesList,
  loading,
  refresh,
  error,
  coopName,
  sortParams,
  pageNumber,
  pageSize,
  filters,
  totalCount,
  statusesOptions,
  typesOptions,
  downloadLink,
  handleChangeSortParams,
  handleChangeInput,
  handleChangeMultiSelect,
  handleResetFilters,
  handleChangeCurrentPage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const inputProps: InputProps = useMemo(
    () => ({
      disableUnderline: true,
      classes: {
        input: classes.input,
      },
    }),
    [classes]
  );

  const textFieldClasses = useMemo(
    () => ({
      root: classes.textFieldRoot,
    }),
    [classes]
  );

  const multiSelectCls = useMemo(
    () => ({
      outputWrapper: classes.multiSelect,
      output: classes.multiSelectOutput,
      outputLabel: classes.multiSelectLabel,
    }),
    [classes]
  );

  const isActiveStatus = useCallback(
    (item: Option) => isItemInList(filters.uds_spacestatuscode, item),
    [filters.uds_spacestatuscode]
  );

  const isActiveType = useCallback(
    (item: Option) => isItemInList(filters.uds_spacetypecode, item),
    [filters.uds_spacetypecode]
  );

  const renderItem = useCallback((item: Option) => item.Label, []);

  if (error) {
    return <Error />;
  }

  if (loading) {
    return <PageLoader />;
  }

  const renderBody = () => {
    return spacesList.map((space) => {
      return (
        <TableRow key={space.Id}>
          <DefaultTableCell>{_get(space, 'Cooperative.Name')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'Address')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'PostalArea')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'Name')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'Type.Label')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'Size')}</DefaultTableCell>
          <DefaultTableCell>{_get(space, 'Status.Label')}</DefaultTableCell>
          <DefaultTableCell>
            {Array.isArray(space.Tenants) &&
              space.Tenants.map((tenant) => tenant.Name).join(', ')}
          </DefaultTableCell>
          <DefaultTableCell align="right">
            {space.TenantRentContractStartDate &&
              customFormat(new Date(space.TenantRentContractStartDate))}
          </DefaultTableCell>
          <DefaultTableCell align="right">
            {space.TenantRentContractEndDate &&
              customFormat(new Date(space.TenantRentContractEndDate))}
          </DefaultTableCell>
        </TableRow>
      );
    });
  };

  return (
    <>
      <PageTitle />
      <div>
        <FiltersBar className={classes.filtersBar}>
          <ControlWrapper>
            <ButtonShape
              className={classes.resetBtn}
              onClick={handleResetFilters}
            >
              {t('#portal.investor.space.clearfilters')}
            </ButtonShape>
          </ControlWrapper>
          <ControlWrapper>
            <Download
              name={`_${coopName} - Tilat`}
              url={downloadLink}
              extension="xls"
            />
          </ControlWrapper>
        </FiltersBar>
        <div className={classes.overflow}>
          <Table>
            <TableHeader
              order={sortParams.order}
              orderBy={sortParams.orderBy}
              handleChangeSortParams={handleChangeSortParams}
              tableCells={investorSpaceTableCells}
            />
            <TableBody>
              <TableRow>
                <StyledTableCell>
                  <div className={classes.fieldWrapper}>
                    <TextField
                      name="uds_cooperativeid"
                      value={filters.uds_cooperativeid}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      classes={textFieldClasses}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div className={classes.fieldWrapper}>
                    <TextField
                      name="uds_accessaddress"
                      value={filters.uds_accessaddress}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      classes={textFieldClasses}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div className={classes.fieldWrapper}>
                    <TextField
                      name="uds_spaceaddresscity"
                      value={filters.uds_spaceaddresscity}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      classes={textFieldClasses}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div className={classes.fieldWrapper}>
                    <TextField
                      name="uds_name"
                      value={filters.uds_name}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      classes={textFieldClasses}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={clsx(
                      classes.fieldWrapper,
                      classes.multipleSelectWrapper
                    )}
                  >
                    <MultipleSelect
                      name="uds_spacetypecode"
                      value={filters.uds_spacetypecode}
                      options={typesOptions}
                      isActive={isActiveType}
                      onChange={handleChangeMultiSelect}
                      renderItem={renderItem}
                      renderValue={renderItem}
                      cls={multiSelectCls}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: 155 }}>
                  <div className={clsx(classes.fieldWrapper, classes.center)}>
                    <TextField
                      type="number"
                      className={classes.numberInput}
                      name="uds_squaremeters_min"
                      value={filters.uds_squaremeters_min}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      placeholder="min"
                    />
                    <span className={classes.divider}>&mdash;</span>
                    <TextField
                      type="number"
                      className={classes.numberInput}
                      name="uds_squaremeters_max"
                      value={filters.uds_squaremeters_max}
                      onChange={handleChangeInput}
                      InputProps={inputProps}
                      placeholder="max"
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <div
                    className={clsx(
                      classes.fieldWrapper,
                      classes.multipleSelectWrapper
                    )}
                  >
                    <MultipleSelect
                      name="uds_spacestatuscode"
                      value={filters.uds_spacestatuscode}
                      options={statusesOptions}
                      isActive={isActiveStatus}
                      onChange={handleChangeMultiSelect}
                      renderItem={renderItem}
                      renderValue={renderItem}
                      cls={multiSelectCls}
                    />
                  </div>
                </StyledTableCell>
                <StyledTableCell></StyledTableCell>
                <StyledTableCell></StyledTableCell>
                <StyledTableCell></StyledTableCell>
              </TableRow>
              {refresh ? null : renderBody()}
            </TableBody>
          </Table>
        </div>
        {refresh ? (
          <Centered className={classes.mt20}>
            <Loader themeColor="darkBlue" size={30} />
          </Centered>
        ) : null}
        {refresh ? null : (
          <div className={classes.paginationWrapper}>
            <TablePagination
              onChange={handleChangeCurrentPage}
              currentPage={pageNumber}
              totalPages={Math.ceil(totalCount / pageSize)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(InvestorSpaceGrid);
